import React from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { cva } from 'class-variance-authority';
import {
  KoobSidebar,
  KoobNavbarUserDropdown,
  KoobOrganizationPicker,
  KoobOrganizationRequiredPrompt,
  useKoobSidebarCollapsed,
} from '@koob/ui';
import {
  closeTripWizard,
  openTripWizard,
  useTripWizardOpen,
} from '@koob/ui/src/features/tripWizard';
import DesignTripFolderModal from '../../containers/TripDesigner/DesignTrip/DesignTripFolderModal';
import { ActiveFolderCard } from '@koob/booking';
import {
  getFolderStatus,
  ORGANIZATION_REQUIRED_EVENT,
  switchActingOrganization,
} from '@koob/utils';
import { useTranslation } from 'react-i18next';
import { useApp, useAuth, useBooking, useExperienceBooking } from '../../hooks';
import { useActiveFolder, useKoobStore } from 'store';
import { useAsync } from 'react-async';
import { getRequestAccess } from 'api/toRequestAccess';
import { revokeToken } from 'api/auth';
import { KoobStateEnum } from 'utils';
import { useEffect, useRef, useState } from 'react';
import KoobNavbarTripDesigner from './KoobNavbarTripDesigner';
import NotificationsDropdown from '../NotificationsDropdown';
import ExperiencesBasket from '../Experience/ExperiencesBasket';
import BrowseFoldersModal from './BrowseFoldersModal';

// Map a snake_case backend role (e.g. 'to_admin') to the camelCase key used in
// the `user:roles` translation namespace (e.g. 'toAdmin').
const roleToTranslationKey = role =>
  role?.replace(/_([a-z])/g, (_, char) => char.toUpperCase());

export default function KoobLayout({
  children,
  horizontalPadding = true,
  variant,
}) {
  const {
    currentOrganization,
    currentOrganizationIdInt,
    user,
    currentUserRole,
    isAllowedTo,
  } = useApp();
  const { logout, impersonating, setImpersonationToken } = useAuth();
  const { t } = useTranslation([
    'mainSidebar',
    'tripDesigner',
    'impersonation',
  ]);
  const navigate = useNavigate();
  const { resetContext, setFilters } = useBooking();
  const {
    resetContext: resetExperienceContext,
    basket,
    setExperienceFilters,
  } = useExperienceBooking();
  const [sidebarCollapsed] = useKoobSidebarCollapsed();
  // The composer's trip header owns the top bar — hide the basket and bell.
  const onTripDesigner = useLocation().pathname.startsWith(
    '/trip-designer/compose',
  );
  const [basketOpen, setBasketOpen] = useState(false);
  const browseFoldersOpen = useKoobStore(s => s.browseFoldersOpen);
  const setBrowseFoldersOpen = useKoobStore(s => s.setBrowseFoldersOpen);
  const wizardOpen = useTripWizardOpen();

  const activeFolder = useActiveFolder();
  const closeFolder = useKoobStore(s => s.closeFolder);
  const openFolder = useKoobStore(s => s.openFolder);

  const activeFolderId = activeFolder?.id;
  const previousFolderId = useRef(activeFolderId);
  useEffect(() => {
    if (previousFolderId.current && !activeFolderId) {
      setFilters(current => ({ ...current, rooms: undefined }));
      setExperienceFilters(current => ({
        ...current,
        experienceComposition: undefined,
      }));
    }
    previousFolderId.current = activeFolderId;
  }, [activeFolderId, setFilters, setExperienceFilters]);

  // Same flow as UserImpersonationAlert: revoke the impersonation token then
  // return to the real account, or log out if both tokens match.
  const stopImpersonating = async () => {
    if (
      localStorage.getItem('accessToken') ===
      localStorage.getItem('impersonationToken')
    ) {
      logout();
      return;
    }
    try {
      await revokeToken(true);
    } catch (error) {
      console.error(
        `An error occurred while revoking the token: ${error.message}`,
      );
    } finally {
      setImpersonationToken('');
      window.location.href = '/';
    }
  };

  const [isValidTripDesignerRequest, setIsValidTripDesignerRequest] = useState(
    localStorage.getItem('tripDesignerRequestState') ===
      KoobStateEnum.VALIDATED,
  );

  const { data } = useAsync({
    promiseFn: getRequestAccess,
  });

  useEffect(() => {
    if (!data) return;
    const requestAccess = data?.data;
    const isValid =
      requestAccess?.tripDesignerRequestState === KoobStateEnum.VALIDATED;
    const state = requestAccess?.tripDesignerRequestState ?? '';

    setIsValidTripDesignerRequest(isValid);
    localStorage.setItem('tripDesignerRequestState', state);
  }, [data]);

  const links = [
    {
      to: '/folders',
      icon: 'far fa-folder-user',
      label: t('folders'),
    },
    {
      to: '/hotels',
      icon: 'far fa-bed',
      label: t('hotels'),
      end: true,
      onClick: () => resetContext(),
    },
    {
      to: '/experiences',
      icon: 'far fa-layer-group',
      label: t('experiences'),
      end: true,
      onClick: () => resetExperienceContext(),
    },
  ];

  const tripDesignerLink = {
    // KD-3723: the index route is the Discover / request-access page, which a
    // validated organization must never land on.
    to: isValidTripDesignerRequest ? '/trip-designer/trips' : '/trip-designer',
    icon: 'far fa-cube',
    label: t('tripDesigner'),
    dataCy: 'trip-designer',
  };

  if (isValidTripDesignerRequest) {
    // Trip Designer is pinned open: children always visible, no chevron.
    tripDesignerLink.alwaysExpanded = true;
    tripDesignerLink.children = [
      {
        // Opens over the current page instead of navigating away.
        onClick: openTripWizard,
        label: t('designTrip'),
        activeMatch: ['/trip-designer/compose'],
        disabled: !activeFolder,
        disabledTip: activeFolder
          ? undefined
          : t('tripDesigner:compose.clientFolderRequired'),
        onDisabledClick: () => setBrowseFoldersOpen(true, 'designTrip'),
      },
      {
        to: '/trip-designer/trips',
        label: t('dmcTemplates'),
      },
      {
        to: '/trip-designer/library',
        label: t('library'),
      },
    ];
  }

  links.push(tripDesignerLink);

  links.push({
    to: '/dmcs',
    icon: 'far fa-link',
    label: t('dmcs'),
  });

  const userName = user?.firstName
    ? `${user.firstName} ${user.lastName || ''}`
    : user?.email;

  const organizationName =
    currentOrganization?.displayName || user?.organizationName;

  const roleKey = roleToTranslationKey(currentUserRole);
  const roleLabel = roleKey ? t(`roles.${roleKey}`, { ns: 'user' }) : undefined;

  // KD-3391: the trip designer variant owns the whole page padding (p-5) and
  // spaces the filter card / compose grid with gap-5.
  const container = cva([], {
    variants: {
      variant: {
        default: 'py-5',
        // Viewport-locked: 100vh minus the fixed 80px navbar and the shell's
        // pb-5 — the page itself never scrolls, the columns do.
        tripDesigner: 'flex h-[calc(100vh-6.25rem)] flex-col gap-5 p-5',
      },
      horizontalPadding: {
        true: '',
      },
    },
    compoundVariants: [
      { variant: 'default', horizontalPadding: true, class: 'px-6 lg:px-8' },
    ],
    defaultVariants: { variant: 'default' },
  });

  return (
    <div className="min-h-screen bg-gray-50 pb-5 flex flex-col">
      <KoobSidebar
        sections={[{ title: null, links }]}
        bottomSlot={collapsed => (
          <ActiveFolderCard
            folder={activeFolder}
            status={getFolderStatus(activeFolder)}
            collapsed={collapsed}
            onOpen={() => navigate(`/folders/${activeFolder?.id}`)}
            onClose={closeFolder}
            onBrowse={() => setBrowseFoldersOpen(true)}
            onDropFolder={openFolder}
          />
        )}
        router={{ NavLink, useLocation }}
      />

      <div
        className={[
          'transition-all duration-300',
          sidebarCollapsed ? 'ml-16' : 'ml-72',
        ].join(' ')}
      >
        {/* Top bar: trip designer + notifications + basket + user block */}
        <div
          className={[
            'fixed top-0 right-0 z-sticky h-20 flex items-center justify-end gap-4 px-6 bg-white border-b border-border transition-all duration-300',
            sidebarCollapsed ? 'left-16' : 'left-72',
          ].join(' ')}
        >
          {/* KD-3391: on compose, the page portals its trip header
              (ComposeHeader) into this slot */}
          <div className="min-w-0 flex-1" id="koob-navbar-trip-designer-slot">
            <KoobNavbarTripDesigner />
          </div>

          {!onTripDesigner && (
            <>
              <NotificationsDropdown />

              <button
                onClick={() => setBasketOpen(!basketOpen)}
                className="relative inline-flex items-center justify-center rounded-md p-2 text-gray-400 hover:text-gray-500 focus:outline-none border-none bg-transparent cursor-pointer"
                aria-label="Basket"
              >
                <i
                  className="far fa-cart-shopping text-2xl"
                  aria-hidden="true"
                  data-testid="basket"
                />
                {basket.length > 0 && (
                  <div className="absolute inline-flex items-center justify-center w-5 h-5 text-xs font-medium text-white bg-orange-500 border-2 border-white rounded-full -top-1 -right-1">
                    {basket.length}
                  </div>
                )}
              </button>
            </>
          )}

          {/* The one organization picker (KD-3735): every screen follows it. */}
          <KoobOrganizationPicker
            className="w-56"
            organizations={user?.organizations ?? []}
            currentOrganizationId={currentOrganization?.organizationId}
            onChange={switchActingOrganization}
            label={t('organizationPicker')}
            groupLabel={name => t('allOrganizationsOf', { name })}
          />

          {/* Separator between the icon buttons and the user block */}
          <span className="w-px h-8 bg-gray-200" aria-hidden="true" />

          <KoobNavbarUserDropdown
            name={userName}
            email={user?.email}
            organization={organizationName}
            role={roleLabel}
            impersonating={impersonating}
            profileLabel={t('myProfile')}
            organizationLabel={t('myOrganization')}
            travelDiaryContentLabel={t('travelDiaryContent')}
            travelDiaryThemeLabel={t('travelDiaryTheme', 'Travel Diary Theme')}
            auditLogLabel={t('auditLog')}
            supportLabel={t('userGuide')}
            logoutLabel={t('signOut')}
            impersonatingLabel={t('impersonation:status')}
            impersonatingBannerLabel={t('impersonation:actingAs')}
            stopImpersonatingLabel={t('impersonation:stop')}
            onStopImpersonating={stopImpersonating}
            onProfile={() => navigate('/user')}
            onOrganization={() =>
              navigate(`/organizations/${currentOrganizationIdInt}`)
            }
            onTravelDiaryContent={
              isAllowedTo('readTravelDiaryContent')
                ? () => navigate('/travel-diary-content-library')
                : undefined
            }
            onTravelDiaryTheme={
              currentUserRole === 'to_admin'
                ? () => navigate('/travel-diary-settings')
                : undefined
            }
            onAuditLog={
              isAllowedTo('readAuditLog')
                ? () => navigate('/audit-log')
                : undefined
            }
            onSupport={() =>
              window.open('https://support.koob.tech/en/', '_blank')
            }
            onLogout={logout}
          />
        </div>

        {basketOpen && (
          <>
            <div
              onClick={() => setBasketOpen(false)}
              className="fixed inset-0 bg-gray-500 bg-opacity-75 backdrop-blur-sm z-modal-backdrop"
            />
            <div className="fixed right-0 top-0 z-modal">
              <ExperiencesBasket closeMenu={() => setBasketOpen(false)} />
            </div>
          </>
        )}

        <main
          className={['mt-20', container({ horizontalPadding, variant })].join(
            ' ',
          )}
        >
          {children}
        </main>
      </div>

      <BrowseFoldersModal
        open={browseFoldersOpen}
        setOpen={setBrowseFoldersOpen}
      />

      {/* One instance for the whole app: every entry point calls
          openTripWizard() and it lands over whatever page is showing. */}
      <DesignTripFolderModal open={wizardOpen} close={closeTripWizard} />

      {/* One prompt for the app: a 400 ORGANIZATION_REQUIRED asks which
          organization the action is for and switches the picker (KD-3735). */}
      <KoobOrganizationRequiredPrompt
        organizations={user?.organizations ?? []}
        eventName={ORGANIZATION_REQUIRED_EVENT}
        onChoose={switchActingOrganization}
        title={t('misc:organizationRequiredTitle')}
        text={t('misc:organizationRequiredText')}
      />
    </div>
  );
}
